/**
 * Size of the webaudio analyzer buffer.
 *
 * @type {number}
 */
const WEBAUDIO_ANALYZER_FFT_SIZE = 512;

/**
 * Value of the webaudio analyzer smoothing time parameter.
 *
 * @type {number}
 */
const WEBAUDIO_ANALYZER_SMOOTING_TIME = 0.8;

const MIN_DBFS = -40;
/*
 Среднеквадратичное значение в формате float для сырых байт значений
*/
const calcRms = (byteSignal: Uint8Array) => {
    let sum = 0;

    for (let i = 0; i < byteSignal.length; i++) {
        const value = (byteSignal[i] - 128) / 128; // конвертируем байты в float значения от -1 до 1
        sum += value * value;
    }

    return Math.sqrt(sum / byteSignal.length);
};
/*
 Рассчитывает dbfs по rms. DBFS - цифровые децибелы
*/
const calcDBFS = (rms: number) => {
    return rms > 0 ? 20 * Math.log10(rms) : -Infinity;
};
/**
 * Converts time domain data array to audio level.
 *
 * @param {Uint8Array} samples the time domain data array.
 * @returns {number} the audio level
 */
const timeDomainDataToAudioLevel = (samples: Uint8Array) => {
    const rms = calcRms(samples);
    const dbfs = Math.max(calcDBFS(rms), MIN_DBFS); // DBFS имеет след. диапазон [MIN_DBFS, 0], где MIN_DBFS считается обычной бытовой тишиной
    const percents = (dbfs - MIN_DBFS) / (0 - MIN_DBFS);

    return percents;
};

const INTERVAL = 100;

type AnalyserCallback = (audioLevel: number) => void;

interface AudioAnalyserOptions {
    everyTickCallback?: boolean;
    tickInterval?: number;
    callback: AnalyserCallback;
}

export class AudioLevelAnalyser {
    private _analyser: AnalyserNode;
    private _audioLevel = 0;
    private _analyserIntervalId: Maybe<number> = undefined;
    private _callbacks = new Set<AnalyserCallback>();
    private _audioDataArray: Uint8Array;

    constructor(audioContext: AudioContext) {
        this._analyser = audioContext.createAnalyser();
        this._analyser.smoothingTimeConstant = WEBAUDIO_ANALYZER_SMOOTING_TIME;
        this._analyser.fftSize = WEBAUDIO_ANALYZER_FFT_SIZE;
        this._audioDataArray = new Uint8Array(this._analyser.frequencyBinCount);
    }

    public start(options: AudioAnalyserOptions): void {
        this._callbacks.add(options.callback);

        if (this._analyserIntervalId !== undefined) {
            return;
        }

        this._analyserIntervalId = window.setInterval(() => {
            if (!this._analyser) {
                return;
            }

            this._analyser.getByteTimeDomainData(this._audioDataArray);
            const audioLevel = timeDomainDataToAudioLevel(this._audioDataArray);

            if (audioLevel !== this._audioLevel || options?.everyTickCallback) {
                this._audioLevel = audioLevel;

                for (const callback of this._callbacks) {
                    callback(this._audioLevel);
                }
            }
        }, INTERVAL);
    }

    public stop(callback: AnalyserCallback): void {
        this._callbacks.delete(callback);

        if (this._callbacks.size === 0) {
            clearInterval(this._analyserIntervalId);
            this._analyserIntervalId = undefined;
        }
    }

    public connect(node: AudioNode): void {
        node.connect(this._analyser);
    }

    public disconnect(node: AudioNode): void {
        node.disconnect(this._analyser);
    }
}
